/**
 * Dashboard Value Formatters
 *
 * Shared number/currency/percent formatters used by amCharts 5 charts,
 * AG Grid cell renderers, and KPI card components.
 */

/**
 * Format a numeric value according to the specified format type.
 */
export function formatValue(
  value: unknown,
  format: string,
  currency?: string
): string {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return String(value);

  switch (format) {
    case 'currency':
      return formatCurrency(num, currency);
    case 'percent':
      return formatPercent(num);
    case 'percent_change':
      return formatPercentChange(num);
    case 'number':
      return formatNumber(num);
    default:
      return String(value);
  }
}

export function formatCurrency(value: number, currency: string = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${formatNumber(value)}`;
  }
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

export function formatPercentChange(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Compact format for large numbers (e.g., 1.2M, 45.3K).
 */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Resolve the display symbol for a currency code via Intl.
 * Falls back to '$' for unknown codes.
 */
export function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).formatToParts(0);
    return parts.find(p => p.type === 'currency')?.value ?? '$';
  } catch {
    return '$';
  }
}

/**
 * Build an amCharts 5 numberFormat string from axis format + currency.
 * Returns undefined when no special formatting is needed.
 *
 * @example buildAmChartsNumberFormat('currency', 'CAD') => "'CA$'#,###"
 * @example buildAmChartsNumberFormat('percent')          => "#'%'"
 * @example buildAmChartsNumberFormat('number')           => undefined
 */
export function buildAmChartsNumberFormat(
  format?: string,
  currency?: string
): string | undefined {
  if (format === 'currency' && currency) {
    return `'${currencySymbol(currency)}'#,###`;
  }
  if (format === 'percent') {
    return "#'%'";
  }
  return undefined;
}
